import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
// Notifiche non necessarie per utenti standalone
import { useAnimations } from '../../hooks/useAnimations';
import { usePaginatedExercises } from '../../hooks/usePaginatedExercises';
import SidebarLayout from '../layout/SidebarLayout';
import { formatExerciseDisplay } from '../../utils/exerciseFormatters';
import { 
  ChevronLeft, 
  Plus, 
  Save, 
  Search, 
  Filter, 
  Tag, 
  X, 
  Info, 
  AlarmClock, 
  StickyNote, 
  ChevronDown, 
  ChevronUp,
  Link2, 
  Dumbbell,
  Eye,
  AlertCircle,
  Trash2,
  GripVertical
} from 'lucide-react';
import ImprovedUserExerciseForm from './ImprovedUserExerciseForm';
import NewExerciseSelector from '../common/NewExerciseSelector';
import MuscleBadges from '../common/MuscleBadges';
import { createApiClient } from '../../services/api';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente locale per esercizio ordinabile con drag & drop
const SortableExerciseItem = ({ exercise, previousExercise, onUpdate, onRemove, onToggleDetails, isExpanded, isFirst }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${
        isDragging ? 'shadow-lg' : 'shadow-sm'
      } transition-all duration-200`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Handle per drag */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          
          {/* Immagine esercizio */}
          <div className="w-12 h-12 flex-shrink-0">
            {exercise.immagine_nome ? (
              <img
                src={`/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`}
                alt={exercise.nome}
                className="w-full h-full rounded-lg object-cover bg-gray-100 dark:bg-gray-700"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="w-full h-full rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center hidden"
            >
              <Dumbbell className="h-6 w-6 text-gray-400" />
            </div>
          </div>
          
          {/* Info esercizio */}
          <div className="flex-1">
            <h3 className="font-medium text-gray-900 dark:text-white">{exercise.nome}</h3>
            {exercise.descrizione && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{exercise.descrizione}</p>
            )}
            
            {/* Badge muscoli */}
            <div className="mt-2">
              <MuscleBadges
                primaryMuscle={exercise.primary_muscle}
                secondaryMuscles={exercise.secondary_muscles}
                variant="compact"
                showCategory={false}
              />
            </div>
            
            {/* Badge tipo di serie */}
            <div className="mt-2 flex items-center space-x-2">
              {exercise.set_type && exercise.set_type !== 'normal' && (
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  exercise.set_type === 'superset' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                  exercise.set_type === 'dropset' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                  exercise.set_type === 'circuit' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                  exercise.set_type === 'giant_set' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300'
                }`}>
                  {exercise.set_type === 'superset' ? 'Superset' :
                   exercise.set_type === 'dropset' ? 'Dropset' :
                   exercise.set_type === 'circuit' ? 'Circuit' :
                   exercise.set_type === 'giant_set' ? 'Giant Set' :
                   exercise.set_type}
                </span>
              )}
              {exercise.is_cardio === 1 && (
                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 font-medium">
                  Cardio
                </span>
              )}
              {exercise.is_isometric_scheda === 1 && exercise.is_cardio !== 1 && (
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-medium">
                  Isometrico
                </span>
              )}
              {exercise.linked_to_previous && (
                <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 font-medium">
                  🔗 Collegato
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pulsanti azioni */}
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => onToggleDetails(exercise.id)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          <button
            type="button"
            onClick={() => onRemove(exercise.id)}
            className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Dettagli espandibili */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Serie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Serie
              </label>
              <input
                type="number"
                min="1"
                value={exercise.serie || 3}
                onChange={(e) => onUpdate(exercise.id, 'serie', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            {/* Ripetizioni o Secondi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {(() => {
                  const isCardio = exercise.is_cardio === 1;
                  const isIsometricScheda = exercise.is_isometric_scheda === 1;
                  if (isCardio) return 'Minuti';
                  if (isIsometricScheda) return 'Secondi';
                  return 'Ripetizioni';
                })()}
              </label>
              <input
                type="number"
                min="1"
                value={exercise.ripetizioni || 12}
                onChange={(e) => onUpdate(exercise.id, 'ripetizioni', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                placeholder={(() => {
                  const isCardio = exercise.is_cardio === 1;
                  const isIsometricScheda = exercise.is_isometric_scheda === 1;
                  if (isCardio) return 'Minuti (es. 30)';
                  if (isIsometricScheda) return 'Secondi di tenuta';
                  return 'Numero ripetizioni';
                })()}
              />
            </div>
            
            {/* Peso - nascosto per esercizi cardio */}
            {exercise.is_cardio !== 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Peso (kg)
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={exercise.peso || 0}
                onChange={(e) => onUpdate(exercise.id, 'peso', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            )}
            
            {/* Recupero */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Recupero (s)
              </label>
              <input
                type="number"
                min="0"
                value={exercise.tempo_recupero || 90}
                onChange={(e) => onUpdate(exercise.id, 'tempo_recupero', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          {/* Tipo di serie */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tipo di Serie
            </label>
            <select
              value={exercise.set_type || 'normal'}
              onChange={(e) => onUpdate(exercise.id, 'set_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="normal">Normale</option>
              <option value="superset">Superset</option>
              <option value="dropset">Dropset</option>
              <option value="circuit">Circuit</option>
              <option value="giant_set">Giant Set</option>
            </select>
          </div>
          
          {/* Collegamento all'esercizio precedente */}
          {!isFirst && exercise.set_type && exercise.set_type !== 'normal' && previousExercise && exercise.set_type === previousExercise.set_type && (
            <div className="mt-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id={`linked-${exercise.id}`}
                  checked={exercise.linked_to_previous || false}
                  onChange={(e) => onUpdate(exercise.id, 'linked_to_previous', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor={`linked-${exercise.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Collegato all'esercizio precedente
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Collega questo esercizio al precedente per formare superset, dropset, ecc.
              </p>
            </div>
          )}
          
          {/* Modalità Isometrica (solo se esercizio non è cardio) */}
          {exercise.is_cardio !== 1 && (
            <div className="mt-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id={`is_isometric_scheda-${exercise.id}`}
                  checked={exercise.is_isometric_scheda === 1}
                  onChange={(e) => onUpdate(exercise.id, 'is_isometric_scheda', e.target.checked ? 1 : 0)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor={`is_isometric_scheda-${exercise.id}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Modalità Isometrica (usa secondi invece di ripetizioni)
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                Se attivo, questo esercizio verrà eseguito a tempo (secondi) invece che a ripetizioni
              </p>
            </div>
          )}
          
          {/* Campi Cardio (solo se esercizio è cardio) */}
          {exercise.is_cardio === 1 && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Calorie (opzionale)
                </label>
                <input
                  type="number"
                  min="0"
                  value={exercise.calorie || ''}
                  onChange={(e) => onUpdate(exercise.id, 'calorie', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Calorie bruciate"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  BPM (opzionale)
                </label>
                <input
                  type="number"
                  min="0"
                  value={exercise.bpm || ''}
                  onChange={(e) => onUpdate(exercise.id, 'bpm', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Battiti per minuto"
                />
              </div>
            </div>
          )}
          
          {/* Campo Note - Sistema Note Duali (Standalone: note vanno su user) */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Note
            </label>
            <textarea
              value={exercise.notes?.user || exercise.note || ''}
              onChange={(e) => {
                // Gestione retrocompatibilità: se esiste exercise.note (legacy), convertilo
                const existingNotes = exercise.notes || 
                  (exercise.note ? { trainer: null, user: exercise.note, system: null } : null) ||
                  { trainer: null, user: null, system: null };
                
                // 🔥 DB-001: Mantieni le note esistenti (trainer, system) quando si modifica user
                const newNotes = {
                  trainer: existingNotes.trainer || null,
                  user: e.target.value || null,
                  system: existingNotes.system || null
                };
                onUpdate(exercise.id, 'notes', newNotes);
              }}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Aggiungi note per questo esercizio..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Componente per anteprima scheda
const SchedaPreview = ({ formData, selectedEsercizi, onClose }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center">
              <Eye className="h-6 w-6 mr-2" />
              {t('createWorkoutPage.preview.title')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">{formData.nome}</h3>
              {formData.descrizione && (
                <p className="text-gray-600 dark:text-gray-400">{formData.descrizione}</p>
              )}
            </div>

            <div>
              <h4 className="text-lg font-medium mb-4">{t('createWorkoutPage.preview.exercises')} ({selectedEsercizi.length})</h4>
              <div className="space-y-3">
                {selectedEsercizi.map((exercise, index) => {
                  const isLinked = exercise.linked_to_previous;
                  const isFirstInGroup = !isLinked || index === 0;
                  
                  return (
                    <div key={exercise.id} className="relative">
                      {/* Indicatore di collegamento */}
                      {isLinked && (
                        <div className="absolute -left-4 top-1/2 transform -translate-y-1/2">
                          <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                            <Link2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </div>
                        </div>
                      )}
                      
                      {/* Card esercizio */}
                      <div className={`flex items-center justify-between p-3 rounded-lg ${
                        isLinked 
                          ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 ml-4' 
                          : 'bg-gray-50 dark:bg-gray-700'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {index + 1}.
                          </span>
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="font-medium">{exercise.nome}</p>
                              {exercise.set_type && exercise.set_type !== 'normal' && (
                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
                                  {exercise.set_type === 'superset' ? 'Superset' : 
                                   exercise.set_type === 'circuit' ? 'Circuit' : 
                                   exercise.set_type === 'giant_set' ? 'Giant Set' : exercise.set_type}
                                </span>
                              )}
                              {isLinked && (
                                <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full flex items-center">
                                  <Link2 className="h-3 w-3 mr-1" />
                                  {t('createWorkoutPage.exerciseItem.linked')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{exercise.gruppo_muscolare}</p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {exercise.serie} x {formatExerciseDisplay(exercise.ripetizioni, exercise)}
                          {exercise.peso > 0 && ` @ ${exercise.peso}kg`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              {t('createWorkoutPage.preview.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ImprovedCreaSchedaPage() {
  const { currentUser, token } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  // Sistema di messaggi semplice per utenti standalone (no NotificationContext)
  const { getAnimationClasses, getInteractionClasses } = useAnimations();
  const navigate = useNavigate();
  const api = createApiClient(token);

  // Stati principali
  const [formData, setFormData] = useState({ nome: '', descrizione: '' });
  const [loading, setLoading] = useState(false);
  const [selectedEsercizi, setSelectedEsercizi] = useState([]);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [activeTab, setActiveTab] = useState('selection'); // 'selection' | 'selected'
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // State per filtri avanzati (come in archivio esercizi)
  const [filters, setFilters] = useState({
    equipment: '',
    primaryMuscle: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // WEBAPP-021: Usa hook per caricamento paginato esercizi
  const {
    exercises: esercizi,
    loading: loadingExercises,
    initialLoading: initialLoadingExercises,
    hasMore,
    loadMore,
    search: searchExercises,
    filterByGroup,
    refresh: refreshExercises
  } = usePaginatedExercises({
    initialLimit: 50,
    enableInfiniteScroll: true,
    searchDebounceMs: 500
  });
  
  // Gestisce il cambio filtri da NewExerciseSelector
  const handleFiltersChange = useCallback((newFilters) => {
    const newEquipment = newFilters.equipment || '';
    const newPrimaryMuscle = newFilters.primaryMuscle || '';
    
    setFilters({
      equipment: newEquipment,
      primaryMuscle: newPrimaryMuscle
    });
    
    // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
    const hasFilters = newEquipment || newPrimaryMuscle;
    const hasSearch = searchTerm && searchTerm.trim().length >= 2;
    
    if (hasFilters || hasSearch) {
      searchExercises(hasSearch ? searchTerm : '', {
        equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
        primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
      });
    } else {
      // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
      searchExercises('', {});
    }
  }, [searchExercises, searchTerm]);
  
  // Gestisce la ricerca testuale
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    
    // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
    const hasSearch = term && term.trim().length >= 2;
    const hasFilters = filters.equipment || filters.primaryMuscle;
    
    if (hasSearch || hasFilters) {
      searchExercises(hasSearch ? term.trim() : '', {
        equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
        primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
      });
    }
  }, [searchExercises, filters]);

  // Validazione in tempo reale
  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.nome.trim()) {
      errors.nome = t('createWorkoutPage.validation.nameRequired');
    }
    
    if (selectedEsercizi.length === 0) {
      errors.esercizi = t('createWorkoutPage.validation.selectExercise');
    }
    
    // Validazione esercizi
    selectedEsercizi.forEach((exercise, index) => {
      if (!exercise.serie || exercise.serie < 1) {
        errors[`esercizio_${index}_serie`] = t('createWorkoutPage.validation.invalidSets');
      }
      if (!exercise.ripetizioni || exercise.ripetizioni < 1) {
        errors[`esercizio_${index}_ripetizioni`] = t('createWorkoutPage.validation.invalidReps');
      }
      if (exercise.peso < 0) {
        errors[`esercizio_${index}_peso`] = t('createWorkoutPage.validation.negativeWeight');
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, selectedEsercizi]);

  // Validazione automatica quando cambiano i dati
  useEffect(() => {
    validateForm();
  }, [formData, selectedEsercizi, validateForm]);

  // Pulisci lo stato expandedExercise se l'esercizio non è più selezionato
  useEffect(() => {
    if (expandedExercise && !selectedEsercizi.find(e => e.id === expandedExercise)) {
      setExpandedExercise(null);
    }
  }, [selectedEsercizi, expandedExercise]);

  // Funzione robusta per toggle espansione
  const handleToggleExpanded = (exerciseId) => {
    setExpandedExercise(prev => {
      // Se l'esercizio è già espanso, chiudilo
      if (prev === exerciseId) {
        return null;
      }
      // Altrimenti espandi questo esercizio
      return exerciseId;
    });
  };

  // Gestione selezione esercizi
  const handleSelectEsercizio = (esercizio) => {
    const exists = selectedEsercizi.find(e => e.id === esercizio.id);
    if (exists) {
      setSelectedEsercizi(prev => prev.filter(e => e.id !== esercizio.id));
      // Chiudi l'esercizio se era espanso
      if (expandedExercise === esercizio.id) {
        setExpandedExercise(null);
      }
    } else {
      setSelectedEsercizi(prev => [
        ...prev, 
        { 
          ...esercizio, 
          serie: esercizio.serie_default || (esercizio.is_cardio === 1 ? 1 : 3), // Cardio: sempre 1 serie
          ripetizioni: esercizio.ripetizioni_default || 12, 
          peso: esercizio.peso_default || 0,
          tempo_recupero: esercizio.tempo_recupero || 90,
          note: '', // Mantieni per retrocompatibilità
          notes: { trainer: null, user: null, system: null }, // 🔥 FASE 6: Inizializza notes (Standalone: note vanno su user)
          set_type: 'normal',
          linked_to_previous: false,
          is_isometric_scheda: 0, // Default: non isometrico a livello scheda
          calorie: null, // Default: nessuna caloria
          bpm: null // Default: nessun BPM
        }
      ]);
      // WEBAPP-021: NON cambiare tab automaticamente, come nella Gym
      // setActiveTab('selected');
    }
  };

  // Gestione aggiornamento esercizi
  const updateEsercizioDetail = (id, field, value) => {
    setSelectedEsercizi(prev =>
      prev.map(e => {
        if (e.id !== id) return e;
        
        // Validazione: se esercizio è cardio, non può essere isometrico nella stessa scheda
        if (field === 'is_isometric_scheda' && value === 1 && e.is_cardio === 1) {
          setMessage('Un esercizio cardio non può essere isometrico nella stessa scheda');
          setMessageType('error');
          setTimeout(() => {
            setMessage('');
            setMessageType('');
          }, 3000);
          return e; // Non modificare
        }
        
        // Se set_type è 'normal', linked_to_previous deve essere false
        if (field === 'set_type' && value === 'normal') {
          return { ...e, [field]: value, linked_to_previous: false };
        }
        
        return { ...e, [field]: value };
      })
    );
  };

  // Gestione rimozione esercizi
  const removeEsercizio = (id) => {
    setSelectedEsercizi(prev => prev.filter(e => e.id !== id));
    // Chiudi l'esercizio se era espanso
    if (expandedExercise === id) {
      setExpandedExercise(null);
    }
  };
  
  // Gestione drag and drop
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSelectedEsercizi(prev => {
        const oldIndex = prev.findIndex(item => item.id === active.id);
        const newIndex = prev.findIndex(item => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };


  // Gestione creazione esercizio
  const handleExerciseCreated = async () => {
    setShowAddExerciseForm(false);
    await refreshExercises();
    setMessage(t('createWorkoutPage.messages.exerciseCreated'));
    setMessageType('success');
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  // Gestione submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage(t('createWorkoutPage.validation.fixErrors'));
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    
    try {
      const eserciziConOrdine = selectedEsercizi.map((es, index) => {
        // Gestione retrocompatibilità: se esiste es.note (legacy), convertilo in formato JSON
        let noteValue;
        if (es.notes) {
          // Nuovo formato: oggetto notes
          noteValue = JSON.stringify(es.notes);
        } else if (es.note) {
          // Legacy: stringa semplice, converti in JSON (Standalone: note vanno su user)
          noteValue = JSON.stringify({
            trainer: null,
            user: es.note,
            system: null
          });
        } else {
          // Nessuna nota: JSON vuoto
          noteValue = JSON.stringify({ trainer: null, user: null, system: null });
        }
        
        // Per esercizi cardio: converti minuti in secondi
        let ripetizioni = parseInt(es.ripetizioni) || 0;
        if (es.is_cardio === 1) {
          // Se è cardio, ripetizioni contiene minuti nell'UI, converti in secondi per il DB
          ripetizioni = ripetizioni * 60;
        }
        
        return {
          id: es.id,
          serie: parseInt(es.serie) || (es.is_cardio === 1 ? 1 : 0), // Cardio: sempre 1 serie
          ripetizioni: ripetizioni,
          peso: parseFloat(es.peso) || 0,
          ordine: index + 1,
          tempo_recupero: parseInt(es.tempo_recupero) || 90,
          note: noteValue,
          set_type: es.set_type || 'normal',
          linked_to_previous: es.linked_to_previous ? 1 : 0,
          is_isometric_scheda: es.is_isometric_scheda || 0, // 🔥 DB-001: Usa is_isometric_scheda invece di is_isometric
          calorie: es.calorie || null,
          bpm: es.bpm || null
        };
      });

      const response = await api.post('/create_scheda_standalone.php', {
        user_id: currentUser.user_id,
        nome: formData.nome,
        descrizione: formData.descrizione,
        esercizi: eserciziConOrdine,
      });

      const data = response.data;

      if (data.success) {
        setMessage(t('createWorkoutPage.messages.updateSuccess'));
        setMessageType('success');
        setTimeout(() => navigate('/standalone/visualizza-schede'), 1500);
      } else {
        setMessage(data.message || t('createWorkoutPage.messages.updateError'));
        setMessageType('error');
      }
    } catch (error) {
      console.error('Errore nella creazione scheda:', error);
      // Estrai il messaggio dall'API se disponibile
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Errore nella creazione della scheda';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SidebarLayout>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full overflow-hidden flex flex-col">
        {/* Container principale */}
        <div className="flex flex-col flex-1">
          {/* Messaggio di feedback */}
          {message && (
            <div className={`mx-6 mt-4 p-4 rounded-lg ${
              messageType === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
            }`}>
              <p>{message}</p>
            </div>
          )}
          {/* Header con titolo e pulsanti */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex-shrink-0">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-xl font-semibold text-white">
                {t('createWorkoutPage.title')}
              </h1>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/standalone/visualizza-schede')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`flex items-center px-6 py-2 rounded-lg font-semibold transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-white text-indigo-600 hover:bg-gray-100'
                  }`}
                >
                  {loading ? (
                    <>
                      <Save className="h-4 w-4 mr-2 inline" />
                      {t('createWorkoutPage.buttons.saveWorkout')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2 inline" />
                      {t('createWorkoutPage.buttons.saveWorkout')}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  {t('createWorkoutPage.form.workoutName')}
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className={`w-full px-3 py-2 rounded-md focus:ring-2 focus:ring-white/50 bg-white/20 text-white placeholder-white/60 border ${
                    validationErrors.nome ? 'border-red-300' : 'border-white/30'
                  }`}
                  placeholder={t('createWorkoutPage.form.workoutNamePlaceholder')}
                />
                {validationErrors.nome && (
                  <p className="mt-1 text-sm text-red-200 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.nome}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  {t('createWorkoutPage.form.description')}
                </label>
                <textarea
                  name="descrizione"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  className="w-full px-3 py-2 rounded-md focus:ring-2 focus:ring-white/50 bg-white/20 text-white placeholder-white/60 border border-white/30"
                  rows="2"
                  placeholder={t('createWorkoutPage.form.descriptionPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
            <nav className="flex space-x-4 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('selection')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'selection'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Selezione Esercizi
              </button>
              <button
                onClick={() => setActiveTab('selected')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'selected'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Esercizi Selezionati ({selectedEsercizi.length})
                {selectedEsercizi.length > 0 && (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    • {selectedEsercizi.reduce((total, ex) => total + (ex.serie || 0), 0)} serie totali
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden min-h-0">
            {activeTab === 'selection' ? (
              <div className="h-full min-h-0 overflow-hidden">
                  <NewExerciseSelector
                    searchTerm={searchTerm}
                    exercises={esercizi}
                    selectedExercises={selectedEsercizi}
                    onSelectExercise={handleSelectEsercizio}
                    onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                    loading={loadingExercises || initialLoadingExercises}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                    onFiltersChange={handleFiltersChange}
                    onSearchChange={handleSearchChange}
                  />
              </div>
            ) : (
              <div className="h-full overflow-y-auto p-6">
                {validationErrors.esercizi && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      {validationErrors.esercizi}
                    </p>
                  </div>
                )}

                {selectedEsercizi.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={selectedEsercizi.map(e => e.id)}
                      strategy={verticalListSortingStrategy}
                    >
                  <div className="space-y-4">
                        {selectedEsercizi.map((exercise, index) => (
                          <SortableExerciseItem
                            key={exercise.id}
                            exercise={exercise}
                            previousExercise={index > 0 ? selectedEsercizi[index - 1] : null}
                            onUpdate={updateEsercizioDetail}
                            onRemove={removeEsercizio}
                            onToggleDetails={handleToggleExpanded}
                            isExpanded={expandedExercise === exercise.id}
                            isFirst={index === 0}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-center py-12">
                    <Dumbbell className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('createWorkoutPage.selectedExercises.noExercisesSelected')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddExerciseForm && (
        <ImprovedUserExerciseForm
          onClose={() => setShowAddExerciseForm(false)}
          onSuccess={handleExerciseCreated}
        />
      )}

      {showPreview && (
        <SchedaPreview
          formData={formData}
          selectedEsercizi={selectedEsercizi}
          onClose={() => setShowPreview(false)}
        />
      )}
    </SidebarLayout>
  );
}
